import type { ReactNode } from "react";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";

type FooterAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Primary only: while disabled, stay focusable (`aria-disabled`, a click is a no-op) — PrimaryButton's opt-in. */
  focusableWhenDisabled?: boolean;
  loading?: boolean;
  testId?: string;
};

type StickyFormFooterProps = {
  /** Left side: a status line (seats, counts). */
  summary?: ReactNode;
  secondary?: FooterAction;
  primary: FooterAction;
};

/**
 * Footer that stays at the bottom of the scrolling page: summary left,
 * Cancel + the primary action right (stacked, primary on top, below `sm`).
 */
export function StickyFormFooter({ summary, secondary, primary }: StickyFormFooterProps) {
  return (
    <div className="sticky bottom-0 z-10 px-4 py-3 border-t outline-primary surface-primary flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="min-w-0 flex-1 body-3-regular font-tertiary" aria-live="polite">
        {summary}
      </div>
      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
        {secondary && (
          <SecondaryButton
            text={secondary.label}
            onClick={secondary.onClick}
            disabled={secondary.disabled}
            size="medium"
            testId={secondary.testId}
          />
        )}
        <PrimaryButton
          text={primary.label}
          onClick={primary.onClick}
          disabled={primary.disabled}
          focusableWhenDisabled={primary.focusableWhenDisabled}
          loading={primary.loading}
          size="medium"
          testId={primary.testId}
        />
      </div>
    </div>
  );
}
